/**
 * Shared Keycloak AuthContext for IT-SCE Portal System
 * 
 * This context provides authentication for all portals using Keycloak with PKCE.
 * Based on official Keycloak JavaScript Adapter documentation:
 * https://www.keycloak.org/docs/latest/securing_apps/index.html#_javascript_adapter
 * 
 * Key Features:
 * - PKCE (Proof Key for Code Exchange) enabled by default
 * - Automatic token refresh
 * - Role-based access control
 * - Axios integration for API calls
 * 
 * Usage:
 * ```tsx
 * import { KeycloakAuthProvider, useAuth } from '@it-sce/shared/contexts/KeycloakAuthContext'
 * 
 * // In your App.tsx:
 * <KeycloakAuthProvider
 *   keycloakUrl="https://auth.it-sce.de"
 *   realm="IT-SCE"
 *   clientId="your-client-id"
 * >
 *   <YourApp />
 * </KeycloakAuthProvider>
 * 
 * // In your components:
 * const { authenticated, user, login, logout } = useAuth()
 * ```
 */

import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react'
import Keycloak from 'keycloak-js'
import axios, { AxiosInstance } from 'axios'
import {
  type AuthStorageOptions,
  authStorageKey,
  storeAccessToken,
  clearAccessToken,
  SEED_TOKENS_KEY,
  CHECKOUT_TOKENS_KEY,
} from './authStorage'

export interface KeycloakUser {
  sub?: string
  email?: string
  emailVerified?: boolean
  username?: string
  preferredUsername?: string
  firstName?: string
  givenName?: string
  lastName?: string
  familyName?: string
  name?: string
  roles?: string[]
  groups?: string[]
}

export interface KeycloakAuthContextType {
  keycloak: Keycloak | null
  authenticated: boolean
  loading: boolean
  token: string | null
  refreshToken: string | null
  user: KeycloakUser | null
  hasRole: (role: string) => boolean
  hasAnyRole: (roles: string[]) => boolean
  hasGroup: (group: string) => boolean
  login: (redirectUri?: string, idpHint?: string) => void
  register: (redirectUri?: string) => void
  logout: (redirectUri?: string) => void
  updateToken: (minValidity?: number) => Promise<boolean>
  api: AxiosInstance | null
  saveSessionForExternalNavigate: () => void
}

const KeycloakAuthContext = createContext<KeycloakAuthContextType>({
  keycloak: null,
  authenticated: false,
  loading: true,
  token: null,
  refreshToken: null,
  user: null,
  hasRole: () => false,
  hasAnyRole: () => false,
  hasGroup: () => false,
  login: () => {},
  register: () => {},
  logout: () => {},
  updateToken: async () => false,
  api: null,
  saveSessionForExternalNavigate: () => {},
})

export const useAuth = () => {
  const context = useContext(KeycloakAuthContext)
  if (!context) {
    throw new Error('useAuth must be used within KeycloakAuthProvider')
  }
  return context
}

export interface KeycloakAuthProviderProps {
  children: ReactNode
  keycloakUrl: string
  realm: string
  clientId: string
  onLoad?: 'login-required' | 'check-sso'
  enableLogging?: boolean
  apiBaseUrl?: string
  portalName?: string
  idpHint?: string  // When set, skips the IdP selection page and goes directly to this IdP
  /** TQ #409: Praefix fuer alle Storage-Schluessel (Pfad-Instanz: '<kennung>:'), Default '' */
  storageKeyPrefix?: string
  /** TQ #409: false = Access-Token nie in localStorage (Pfad-Instanz). Default true (Legacy). */
  accessTokenInLocalStorage?: boolean
  /** TQ #409: Ziel nach Logout, Default window.location.origin (Pfad-Instanz: Origin + /<kennung>/) */
  logoutRedirectUri?: string
}

/** Liefert redirectUri nur, wenn es ein nicht-leerer String ist (sonst undefined = Default des Aufrufers). */
function validRedirectUri(redirectUri: unknown): string | undefined {
  return typeof redirectUri === 'string' && redirectUri ? redirectUri : undefined
}

export const KeycloakAuthProvider = ({
  children,
  keycloakUrl,
  realm,
  clientId,
  onLoad = 'check-sso',
  enableLogging = false,
  apiBaseUrl,
  portalName = 'Portal',
  idpHint,
  storageKeyPrefix = '',
  accessTokenInLocalStorage = true,
  logoutRedirectUri,
}: KeycloakAuthProviderProps) => {
  const storageOpts: AuthStorageOptions = { keyPrefix: storageKeyPrefix, accessTokenInLocalStorage }
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null)
  const [authenticated, setAuthenticated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [user, setUser] = useState<KeycloakUser | null>(null)

  // 2025-11-26: CRITICAL FIX - Use useRef instead of useState for Axios instance
  // Problem: Axios instances are function-objects with callable properties (.get, .post, etc.)
  // React useState serializes state, causing function properties to be lost
  // Symptom: "TypeError: api.get is not a function"
  // Solution: useRef stores the reference without serialization
  // Impact: Fixes API calls in both Admin Portal and Customer Portal
  // Related: See docs/SHARED-COMPONENTS-IMPACT-ANALYSIS.md
  const apiRef = useRef<AxiosInstance | null>(null)

  // TQ #337-Folge (Login-Loop lokal, 30.09.2026): React 18 StrictMode
  // (main.tsx) invoked diesen Effect im Dev-Modus zweimal beim Mount. Ohne
  // Guard laufen zwei kc.init()-Durchlaeufe: der erste verarbeitet
  // ?code&state und entfernt sie per history.replaceState; der zweite findet
  // dann keinen Code mehr, haelt sich fuer "keine SSO-Session" und loest bei
  // onLoad='check-sso' (checkLoginIframe:false, kein silentCheckSsoRedirectUri)
  // einen Top-Window-Redirect mit prompt=none zu Keycloak aus. Mit warmer
  // SSO-Session kommt der sofort mit neuem Code zurueck -> Reload -> erneutes
  // Doppel-Invoke -> Loop (alle ~0.25s, NUR im Vite-Dev-Server, da StrictMode
  // das doppelte Effect-Invoke in Production-Builds nicht macht). Keycloak-js
  // vertraegt kein zweites gleichzeitiges init() auf demselben Tab -> Guard
  // stellt sicher, dass die eigentliche Init-Logik pro Tab nur einmal laeuft.
  const initStarted = useRef(false)

  useEffect(() => {
    if (initStarted.current) return
    initStarted.current = true

    const initKeycloak = async () => {
      console.log(`[${portalName} Auth] Initializing Keycloak...`)
      
      const keycloakConfig = {
        url: keycloakUrl,
        realm: realm,
        clientId: clientId,
      }
      
      if (enableLogging) {
        console.log(`[${portalName} Auth] Config:`, keycloakConfig)
      }

      const kc = new Keycloak(keycloakConfig)

      try {
        // Initialize Keycloak with PKCE
        // According to Keycloak docs, pkceMethod alone enables Standard Flow with PKCE
        // No need for 'flow' option - it's automatically set when pkceMethod is provided

        // DEBUG: Log what we're about to pass to kc.init()
        // keycloak-js v26: useNonce removed (always enabled), enableLogging removed from init
        // pkceMethod S256 is now the default but we keep it explicit for clarity
        const initOptions: import('keycloak-js').KeycloakInitOptions = {
          onLoad: onLoad,
          checkLoginIframe: false,
          pkceMethod: 'S256' as const,
          redirectUri: window.location.href,
        }

        // TQ #7057 (Option A — Direct Grant auto-login after registration):
        // The registration flow seeds freshly-minted OIDC tokens into
        // sessionStorage under 'itsce_kc_seed_tokens' and then navigates to '/'.
        // Adopt them ONCE here so keycloak-js validates+establishes the session
        // without a redirect. One-shot: the seed is removed immediately so a
        // reload never re-adopts a stale/expired token. On any parse failure we
        // fall through to the normal login flow (token left undefined).
        try {
          const seedKey = authStorageKey(storageOpts, SEED_TOKENS_KEY)
          const seedRaw = sessionStorage.getItem(seedKey)
          if (seedRaw) {
            sessionStorage.removeItem(seedKey) // one-shot, remove before use
            const seed = JSON.parse(seedRaw) as {
              access_token?: string
              refresh_token?: string
              id_token?: string
              expires_in?: number
            }
            if (seed?.access_token) {
              initOptions.token = seed.access_token
              if (seed.refresh_token) initOptions.refreshToken = seed.refresh_token
              if (seed.id_token) initOptions.idToken = seed.id_token
              console.log(`[${portalName} Auth] 🌱 Seeded tokens from registration (TQ #7057) — adopting session without redirect`)
            }
          }
        } catch (seedErr) {
          console.warn(`[${portalName} Auth] ⚠️ Seed-token parse failed, falling back to normal login:`, seedErr)
        }

        // TQ #8883: Restore KC session after external redirect (Stripe checkout).
        // Before navigating to Stripe, components call saveSessionForExternalNavigate()
        // which saves tokens to sessionStorage. On return, we restore them here so
        // check-sso succeeds without a full re-login (same one-shot pattern as #7057).
        if (!initOptions.token) {
          try {
            const checkoutKey = authStorageKey(storageOpts, CHECKOUT_TOKENS_KEY)
            const checkoutRaw = sessionStorage.getItem(checkoutKey)
            if (checkoutRaw) {
              sessionStorage.removeItem(checkoutKey) // one-shot
              const ct = JSON.parse(checkoutRaw) as {
                access_token?: string
                refresh_token?: string
                id_token?: string
              }
              if (ct?.access_token) {
                initOptions.token = ct.access_token
                if (ct.refresh_token) initOptions.refreshToken = ct.refresh_token
                if (ct.id_token) initOptions.idToken = ct.id_token
                console.log(`[${portalName} Auth] 🔄 Restoring session after external redirect (TQ #8883)`)
              }
            }
          } catch (checkoutErr) {
            console.warn(`[${portalName} Auth] ⚠️ Checkout token restore failed, falling back to normal login:`, checkoutErr)
          }
        }

        // SECURITY (TQ #7057 security-fix F2): never dump the seeded OIDC tokens
        // into the browser DevTools console — redact token/refreshToken/idToken
        // before logging the init options.
        const initOptionsLog = {
          ...initOptions,
          token: initOptions.token ? '[REDACTED]' : undefined,
          refreshToken: initOptions.refreshToken ? '[REDACTED]' : undefined,
          idToken: initOptions.idToken ? '[REDACTED]' : undefined,
        }
        console.log(`[${portalName} Auth] 🔍 INIT OPTIONS:`, JSON.stringify(initOptionsLog, null, 2))
        console.log(`[${portalName} Auth] 🔍 redirectUri type:`, typeof initOptions.redirectUri)
        console.log(`[${portalName} Auth] 🔍 redirectUri value:`, initOptions.redirectUri)

        const isAuthenticated = await kc.init(initOptions)

        console.log(`[${portalName} Auth] ✓ Initialization successful`)
        console.log(`[${portalName} Auth] 🔍 Authenticated:`, isAuthenticated)
        console.log(`[${portalName} Auth] 🔍 Token:`, kc.token ? 'Present (length: ' + kc.token.length + ')' : 'NULL')
        console.log(`[${portalName} Auth] 🔍 Refresh Token:`, kc.refreshToken ? 'Present' : 'NULL')
        console.log(`[${portalName} Auth] 🔍 Session State:`, kc.sessionId || 'NULL')

        // DEBUG: Check browser URL for auth params
        const urlParams = new URLSearchParams(window.location.search)
        console.log(`[${portalName} Auth] 🔍 URL has 'code' param:`, urlParams.has('code'))
        console.log(`[${portalName} Auth] 🔍 URL has 'state' param:`, urlParams.has('state'))
        console.log(`[${portalName} Auth] 🔍 URL has 'error' param:`, urlParams.has('error'))

        setKeycloak(kc)

        if (isAuthenticated) {
          console.log(`[${portalName} Auth] 🟢 USER IS AUTHENTICATED - Processing token...`)
          // CRITICAL: Validate that the token is from the correct realm
          // This prevents cross-realm authentication (e.g., Personal-Users token in IT-SCE realm)
          if (kc.tokenParsed) {
            const tokenParsed = kc.tokenParsed as any
            const tokenIssuer = tokenParsed.iss || ''
            const expectedIssuer = `${keycloakUrl}/realms/${realm}`

            console.log(`[${portalName} Auth] 🔍 Token issuer:`, tokenIssuer)
            console.log(`[${portalName} Auth] 🔍 Expected issuer:`, expectedIssuer)

            if (!tokenIssuer.includes(`/realms/${realm}`)) {
              console.warn(`[${portalName} Auth] ⚠️ REALM MISMATCH! Token is from wrong realm!`)
              console.warn(`[${portalName} Auth] ⚠️ Token realm:`, tokenIssuer)
              console.warn(`[${portalName} Auth] ⚠️ Expected realm:`, expectedIssuer)
              console.warn(`[${portalName} Auth] ⚠️ Logging out and forcing re-authentication...`)

              // Logout from the wrong realm and force login to correct realm
              await kc.logout({ redirectUri: window.location.href })
              setAuthenticated(false)
              setLoading(false)
              return
            }

            if (enableLogging) {
              console.log(`[${portalName} Auth] ✓ Realm validation passed`)
              console.log(`[${portalName} Auth] Token parsed:`, tokenParsed)
            }
          }

          setAuthenticated(isAuthenticated)

          // Set tokens (both in state and localStorage for API service compatibility)
          setToken(kc.token || null)
          setRefreshToken(kc.refreshToken || null)

          // Store token in localStorage for API service to use
          if (kc.token) {
            storeAccessToken(storageOpts, kc.token)
          }

          // Extract user info from token
          if (kc.tokenParsed) {
            const tokenParsed = kc.tokenParsed as any
            
            setUser({
              sub: tokenParsed.sub,
              email: tokenParsed.email,
              emailVerified: tokenParsed.email_verified,
              username: tokenParsed.preferred_username,
              preferredUsername: tokenParsed.preferred_username,
              firstName: tokenParsed.given_name,
              givenName: tokenParsed.given_name,
              lastName: tokenParsed.family_name,
              familyName: tokenParsed.family_name,
              name: tokenParsed.name,
              roles: tokenParsed.realm_access?.roles || [],
              groups: tokenParsed.groups || [],
            })
          }

          // 2025-11-26: Setup axios instance with automatic token refresh
          // Creates an Axios client that automatically:
          // 1. Refreshes Keycloak token before requests (if < 30s until expiry)
          // 2. Adds Authorization header to all requests
          // 3. Redirects to login if token refresh fails
          // TODO: After successful stabilization, reduce debug logging to LOG_LEVEL=debug only
          console.log(`[${portalName} Auth] 🌐 API Base URL:`, apiBaseUrl !== undefined ? apiBaseUrl : '(not set)')
          if (apiBaseUrl !== undefined) {
            console.log(`[${portalName} Auth] 🔧 Creating axios instance with baseURL: ${apiBaseUrl}`)
            const apiInstance = axios.create({
              baseURL: apiBaseUrl,
            })

            apiInstance.interceptors.request.use(
              async (config) => {
                console.log(`[${portalName} Auth] 🔍 Interceptor called for: ${config.method?.toUpperCase()} ${config.url}`)
                try {
                  // Refresh token if it expires in less than 30 seconds
                  // This prevents race conditions where token expires during API call
                  await kc.updateToken(30)
                  setToken(kc.token || null)
                  // Update localStorage for API service compatibility
                  if (kc.token) {
                    storeAccessToken(storageOpts, kc.token)
                  }
                  // SECURITY (TQ #7057 security-fix F2): do not leak even a token
                  // prefix into the console — log presence only, not the value.
                  console.log(`[${portalName} Auth] 🔑 Token available:`, kc.token ? 'Yes ([REDACTED])' : 'No')
                  if (kc.token) {
                    config.headers.Authorization = `Bearer ${kc.token}`
                    console.log(`[${portalName} Auth] ✅ Authorization header set`)
                  } else {
                    console.warn(`[${portalName} Auth] ⚠️  No token available - request will fail`)
                  }
                } catch (err) {
                  console.error(`[${portalName} Auth] ❌ Token refresh failed:`, err)
                  // Token refresh failed - redirect to login
                  kc.login({ redirectUri: window.location.href, ...(idpHint ? { idpHint } : {}) })
                }
                return config
              },
              (error) => Promise.reject(error)
            )

            apiRef.current = apiInstance
            console.log(`[${portalName} Auth] ✓ Axios instance configured`)
            console.log(`[${portalName} Auth] 🔍 apiInstance type:`, typeof apiInstance)
            console.log(`[${portalName} Auth] 🔍 apiInstance.get type:`, typeof apiInstance.get)
            console.log(`[${portalName} Auth] 🔍 apiInstance keys:`, Object.keys(apiInstance).slice(0, 10))
            console.log(`[${portalName} Auth] 🔍 apiRef.current.get type:`, typeof apiRef.current?.get)
          }

          // Setup automatic token refresh every 60 seconds
          const refreshInterval = setInterval(() => {
            kc.updateToken(70)
              .then((refreshed) => {
                if (refreshed && enableLogging) {
                  console.log(`[${portalName} Auth] Token refreshed`)
                  setToken(kc.token || null)
                }
              })
              .catch((err) => {
                console.error(`[${portalName} Auth] Token refresh failed:`, err)
                clearInterval(refreshInterval)
                // Don't automatically redirect - let user choose to re-login
              })
          }, 60000)

          // Cleanup on unmount
          return () => clearInterval(refreshInterval)
        } else {
          console.log(`[${portalName} Auth] User not authenticated`)
        }
      } catch (error: any) {
        console.error(`[${portalName} Auth] ✗ Initialization failed:`, error)
        console.error(`[${portalName} Auth] Error details:`, {
          message: error?.message,
          error: error?.error,
          error_description: error?.error_description,
        })
        // Set keycloak instance even on error so login() works
        setKeycloak(kc)
      } finally {
        setLoading(false)
      }
    }

    initKeycloak()
  }, [keycloakUrl, realm, clientId, onLoad, enableLogging, apiBaseUrl, portalName, storageKeyPrefix, accessTokenInLocalStorage])

  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) || false
  }

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => user?.roles?.includes(role))
  }

  const hasGroup = (group: string): boolean => {
    return user?.groups?.includes(group) || false
  }

  const login = (redirectUri?: string, idpHintOverride?: string) => {
    // Nur nicht-leere Strings: ein versehentlich uebergebenes Klick-Event ergaebe sonst "[object Object]"
    redirectUri = validRedirectUri(redirectUri)
    console.log(`[${portalName} Auth] 🔍 login() called with redirectUri:`, redirectUri)
    // CRITICAL: Keycloak adapter falls back to "http://localhost" if no redirectUri
    // We must explicitly pass window.location.href when redirectUri is not provided
    const actualRedirectUri = redirectUri || window.location.href
    // idpHint: call-level override takes precedence over provider-level default
    const actualIdpHint = idpHintOverride || idpHint

    const loginOptions: Record<string, string> = { redirectUri: actualRedirectUri }
    if (actualIdpHint) {
      loginOptions.idpHint = actualIdpHint
    }
    console.log(`[${portalName} Auth] 🔍 LOGIN OPTIONS:`, JSON.stringify(loginOptions, null, 2))
    console.log(`[${portalName} Auth] Redirecting to login with options:`, loginOptions)

    keycloak?.login(loginOptions)
  }

  // B2C self-registration (TQ #5891): keycloak-js login with action=register
  // opens the Keycloak registration form directly. Requires realm
  // registrationAllowed=true (Personal-Users enabled 2026-05-28).
  const register = (redirectUri?: string) => {
    redirectUri = validRedirectUri(redirectUri)
    const actualRedirectUri = redirectUri || window.location.href
    const registerOptions: Record<string, string> = {
      redirectUri: actualRedirectUri,
      action: 'register',
    }
    console.log(`[${portalName} Auth] Redirecting to registration with options:`, registerOptions)
    keycloak?.login(registerOptions)
  }

  const logout = (redirectUri?: string) => {
    // Nur nicht-leere Strings: ein Klick-Event als erstes Argument ergab post_logout_redirect_uri=[object Object]
    redirectUri = validRedirectUri(redirectUri)
    console.log(`[${portalName} Auth] 🔴 LOGOUT REQUESTED`)
    console.log(`[${portalName} Auth] 🔍 Current authenticated state:`, authenticated)
    console.log(`[${portalName} Auth] 🔍 Current token:`, keycloak?.token ? 'Present' : 'NULL')
    console.log(`[${portalName} Auth] 🔍 Current refresh token:`, keycloak?.refreshToken ? 'Present' : 'NULL')

    // Clear token from localStorage
    clearAccessToken(storageOpts)

    // CRITICAL: After logout, redirect to origin (not full URL) to avoid immediate re-login via check-sso
    // Using window.location.origin instead of window.location.href prevents automatic SSO re-login
    const actualRedirectUri = redirectUri || logoutRedirectUri || window.location.origin
    console.log(`[${portalName} Auth] 🔍 Logout redirectUri:`, actualRedirectUri)
    console.log(`[${portalName} Auth] 🔍 window.location.href:`, window.location.href)
    console.log(`[${portalName} Auth] 🔍 window.location.origin:`, window.location.origin)

    console.log(`[${portalName} Auth] 🔴 Calling keycloak.logout() NOW...`)
    keycloak?.logout({ redirectUri: actualRedirectUri })
  }

  // TQ #8883: Save KC tokens before navigating away from the SPA (e.g. to Stripe).
  // On return, the init function restores them from 'itsce_kc_checkout_tokens' so
  // check-sso succeeds without a full re-login round-trip.
  const saveSessionForExternalNavigate = () => {
    const kc = keycloak
    if (kc?.token) {
      try {
        sessionStorage.setItem(authStorageKey(storageOpts, CHECKOUT_TOKENS_KEY), JSON.stringify({
          access_token: kc.token,
          refresh_token: kc.refreshToken,
          id_token: kc.idToken,
        }))
      } catch {
        // sessionStorage may be unavailable (private browsing quota) — ignore
      }
    }
  }

  const updateToken = async (minValidity: number = 30): Promise<boolean> => {
    if (!keycloak) return false
    try {
      const refreshed = await keycloak.updateToken(minValidity)
      if (refreshed) {
        setToken(keycloak.token || null)
        setRefreshToken(keycloak.refreshToken || null)
      }
      return refreshed
    } catch (error) {
      console.error(`[${portalName} Auth] Token update failed:`, error)
      return false
    }
  }

  // Loading screen - IT-SCE Corporate Design
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#F5F7FA'
      }}>
        <div style={{
          background: 'white',
          padding: '2.5rem 3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          textAlign: 'center',
          borderTop: '4px solid #FE4F00'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #E5E7EB',
            borderTop: '3px solid #FE4F00',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ margin: 0, color: '#374151', fontWeight: 500 }}>Authentifizierung prüfen...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    )
  }

  // Login screen - IT-SCE Corporate Design
  if (!authenticated && onLoad === 'login-required') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#F5F7FA'
      }}>
        <div style={{
          background: 'white',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          textAlign: 'center',
          maxWidth: '400px',
          borderTop: '4px solid #FE4F00'
        }}>
          <h2 style={{ marginBottom: '1rem', color: '#1F2937' }}>{portalName}</h2>
          <p style={{ marginBottom: '2rem', color: '#6B7280' }}>
            Bitte melden Sie sich an, um fortzufahren
          </p>
          <button
            onClick={() => login()}
            style={{
              background: '#FE4F00',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: 'pointer',
              fontWeight: 600,
              width: '100%',
              transition: 'background 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#e54500'}
            onMouseOut={(e) => e.currentTarget.style.background = '#FE4F00'}
          >
            Anmelden
          </button>
        </div>
      </div>
    )
  }

  return (
    <KeycloakAuthContext.Provider
      value={{
        keycloak,
        authenticated,
        loading,
        token,
        refreshToken,
        user,
        hasRole,
        hasAnyRole,
        hasGroup,
        login,
        register,
        logout,
        updateToken,
        api: apiRef.current,
        saveSessionForExternalNavigate,
      }}
    >
      {children}
    </KeycloakAuthContext.Provider>
  )
}

export default KeycloakAuthContext
